import { SparksNMN } from '@sparks-notation/core'
import { useSyncExternalStore } from 'react'

/**
 * 乐谱字体（`core-resources/font`，9 个 face、约 15MB；bold 变体刻意不载，见 `core/renderer/FontLoaderProxy.ts` 的注释）的加载状态。
 *
 * **只有「画谱」用得上这批字体。** 站内 UI 文字走的是另一套自托管子集
 * （`/ui-fonts/`，见 `theme/tokens.ts` 的 `SBT Serif` / `SBT Sans`）；
 * 这里的 EOPNumber / Bravura / mscore-20 / Roman / CommonSerif 只出现在谱面里。
 * 所以指南、首页、编曲练习库、登录页**一概不必等它** —— 见 `TestApp` 里那份免字体白名单。
 *
 * ⚠️ 也不能改成「先画、字体到了再重画」，两条理由各自都足够致命：
 *
 *   1. core 渲染器用 canvas `measureText` 量出字形宽度再摆音符
 *      （`core/renderer/backend/DomPaint.ts`），字体没就位就画 = 尺寸全错；
 *      而 `SparksNMNDisplay` / `SparksNMNPreview` 把结果缓存在 `useMemo` 里，
 *      **字体晚到不会触发重画**，错版会一直钉在那儿。
 *   2. 更糟的是 `DomPaint` 里那份 `measureCache` 是**模块级、永不失效**的，
 *      键里只有 (文本, 字体族, 字重, 字号)，**不含"字体就位了没有"**。
 *      一个字形量错，这一对 (文本, 字体) 就**整个会话**都是错的 ——
 *      跨路由、跨重渲染，之后指南再画同一段谱也是错的。
 *
 * 所以唯一正确的做法就是**没就位先别画**，绝不留"先渲染出来再说"的后门。
 *
 * 与 `ensureGlobalStyle()` 一样，加载由 `src/app.tsx` **显式**启动（幂等，重复调无害）——
 * 不放在模块副作用里，免得哪天 import 被摇掉了还没人发现。
 */

/** 相对路径：按当前文档解析，路径与 query 都保留，天然跟着 `--base=./` 的子目录部署走 */
const CORE_FONT_PATH = './core-resources/font'

let started = false
const listeners = new Set<() => void>()

/**
 * 快照**直接读 `FontLoaderProxy` 的 `fontPhase`**，不自建一个 ready 变量：
 * 它是模块级的、单向的（`none → loading → loaded`，永不回头），
 * 于是这个快照天然满足 `useSyncExternalStore` 的两个要求 ——
 * 布尔量，引用稳定；且只翻一次，指南那 165 个 `ScoreCanvas` 各只重渲染一遍，
 * 而不是跟着 11 次进度回调抖 11 轮。
 *
 * 派生自 `getState()` 还有个好处：HMR、或字体早就加载好时才挂载组件，
 * 都不会读到过期值（模块作用域里初始化的副本就会）。
 */
// 测试开关生效标记：?nofont=1 时为 true，getReady 恒真
let fontPhaseForcedReady = false

const getReady = () => fontPhaseForcedReady || SparksNMN.fontLoader.getState() === 'loaded'

/**
 * 进度快照必须也是**稳定引用**：`getSnapshot` 每次返回新对象，
 * React 会当场抛 "The result of getSnapshot should be cached to avoid an infinite loop"。
 * 所以只在数值真的变了时才换新对象。
 */
let progress: {progress: number, total: number} = {progress: 0, total: 0}
const getProgress = () => progress

function emit() {
	for (const listener of listeners) listener()
}

/** 幂等。重复调用只是不动声色地返回。 */
export function startCoreFontLoad() {
	if (started) return
	started = true

	// ── 测试开关：?nofont=1 跳过字体加载，直接放行渲染 ──────────────────
	// 用途：量化"字体闸门"到底占了多少打开时间（用户实测用）。
	// 打开后字体**不下载**，谱面用浏览器默认字体画——间距/字形必然偏差，
	// 但打开速度即"零字体耗时"的基线。⚠️ 仅用于测速对比，不是正式功能。
	if (typeof location !== 'undefined' && /[?&]nofont=1/.test(location.search)) {
		fontPhaseForcedReady = true
		// 字体此刻还没注册任何 FontFace，measureCache 必然是干净的；
		// 之后如果有人手动再触发真加载（刷新去掉参数），会走全新页面，无污染。
		emit()
		return
	}

	SparksNMN.fontLoader.requestFontLoad(CORE_FONT_PATH, () => {
		// 注意顺序：先更新快照再通知订阅者。
		// `requestFontLoad` 在已加载时会**同步**回调，此刻还没有订阅者，emit 空转而已。
		emit()
	}, (now, total) => {
		// 已经就位了就别再为进度抖动了
		if (getReady() || (progress.progress === now && progress.total === total)) return
		progress = {progress: now, total: total}
		emit()
	})
}

function subscribe(listener: () => void) {
	// 兜底：订阅者一定比"加载完"先出现，这里顺手保证加载真的起跑了。
	// `startCoreFontLoad` 自身幂等，所以这一行只是把"忘了在 app.tsx 里启动"
	// 从「谱面永远画不出来」这种**静默**故障，降级成一次布尔判断。
	startCoreFontLoad()
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

/** 字体到位了没有。只要布尔量，不要进度 —— 进度见下。 */
export function useCoreFontsReady() {
	return useSyncExternalStore(subscribe, getReady)
}

/** 进度只给 `CoreFontSplash` 用（它要显示 `N/9`），别的地方别订阅它。 */
export function useCoreFontProgress() {
	return useSyncExternalStore(subscribe, getProgress)
}
